import { Button, TextInput } from "@mantine/core";
import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { authClient } from "../../auth/client";
import { useAuth } from "../../auth/provider";
import { useMessages } from "../../i18n/provider";
import styles from "./auth.module.css";
import { AuthFooterLink, AuthLayout } from "./layout";
import { SocialButtons } from "./social-buttons";

function safeNext(value: string | null): string {
  if (!value) return "/dashboard";
  if (!value.startsWith("/") || value.startsWith("//")) return "/dashboard";
  return value;
}

function errorMessage(
  error: { code?: string; status?: number } | null,
  t: ReturnType<typeof useMessages>,
): string {
  if (!error) return "";
  if (error.status === 429) return t("auth.tooManyAttempts");
  if (error.code === "INVALID_EMAIL_OR_PASSWORD") return t("auth.invalidCredentials");
  return t("auth.genericError");
}

export function SignInPage() {
  const t = useMessages();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = useMemo(
    () => safeNext(new URLSearchParams(location.search).get("next")),
    [location.search],
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t("auth.invalidEmail"));
      return;
    }
    if (password.length < 8) {
      setError(t("auth.passwordShort"));
      return;
    }
    setLoading(true);
    const result = await authClient.signIn.email({ email, password });
    setLoading(false);
    if (result.error) {
      setError(errorMessage(result.error, t));
      return;
    }
    await refresh();
    navigate(next, { replace: true });
  };

  return (
    <AuthLayout
      title={t("auth.welcomeBack")}
      footer={
        <>
          {t("auth.noAccount")} <AuthFooterLink to="/signup" label={t("auth.signUp")} />
        </>
      }
    >
      <SocialButtons next={next} />
      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : null}
      <form className={styles.form} onSubmit={(event) => void submit(event)}>
        <TextInput
          label={t("auth.email")}
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
          required
        />
        <TextInput
          label={t("auth.password")}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.currentTarget.value)}
          required
        />
        <Button type="submit" fullWidth loading={loading}>
          {t("auth.signIn")}
        </Button>
      </form>
      <div style={{ marginTop: 12, textAlign: "center" }}>
        <Link className={styles.link} to="/reset-password">
          {t("auth.forgot")}
        </Link>
      </div>
    </AuthLayout>
  );
}
